import { sql } from "drizzle-orm";

import type { Client } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("feed");

import { client } from "@/config";
import { executeQuery, Tx } from "@/db/drizzleUtils";
import { account, feed, item, workspaceCommandAlias, workspaceMembership } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emitTableUpdates, TableEventManager } from "@/models/eventing/TableEventManager";
import { bulkUpsertWsAudioEncodings } from "@/models/repositories/AudioEncoding";
import { bulkUpsertCallRecord } from "@/models/repositories/CallRecord";
import { bulkUpsertDisplayArtifact } from "@/models/repositories/DisplayArtifact";
import { markPermissionsLoaded } from "@/models/repositories/Feed";
import { bulkUpsertFile } from "@/models/repositories/File";
import { markItemsContentLoaded } from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertPermission } from "@/models/repositories/Permission";
import type { PreferredLanguage } from "../languages";
import { bulkUpsertWsTranscriptions } from "../repositories";
import { downloadOldestFeedItemEvents } from "./bootstrap";

export async function downloadPaginatedFeedPermissions({
  client,
  workspaceId,
  feedId,
}: {
  client: Client;
  workspaceId: string;
  feedId: string;
}): Promise<void> {
  let pageSize = 50000;
  let page = 0;
  while (true) {
    const response = await client.getWorkspaceFeedPermissions({
      workspaceId,
      feedId,
      page,
      pageSize,
    });
    if (response.pageSize < pageSize) {
      pageSize = response.pageSize;
    }
    await bulkUpsertPermission(response.permissions);
    if (response.permissions.length === 0 || response.permissions.length < response.pageSize || page > 1000) {
      break;
    }
    page += 1;
  }
  await markPermissionsLoaded(feedId);
}

export async function downloadFeedItemContent(
  workspaceId: string,
  feedItemIds: string[],
  language?: PreferredLanguage,
  timestamp?: string,
) {
  if (feedItemIds.length === 0) return;

  log.info("[DOWNLOAD CONTENT] Starting download", {
    itemCount: feedItemIds.length,
    itemIds: feedItemIds,
    language,
  });

  const chunkSize = 100;
  const itemIdChunks: string[][] = [];
  for (let i = 0; i < feedItemIds.length; i += chunkSize) {
    itemIdChunks.push(feedItemIds.slice(i, i + chunkSize));
  }

  const eventPromises: Promise<unknown>[] = [];

  await TableEventManager.batch(async () => {
    const contentPromises: Promise<void>[] = [];

    for (const itemChunk of itemIdChunks) {
      eventPromises.push(downloadOldestFeedItemEvents(itemChunk, workspaceId, timestamp, true, 2));
      contentPromises.push(
        client.getFeedItemContent(workspaceId, itemChunk, language, timestamp).then(async (r) => {
          await bulkUpsertWsTranscriptions(r?.transcriptions || [], true);
          await bulkUpsertWsAudioEncodings(r?.audioEncodings || [], true);
          await bulkUpsertFile(r?.files || [], true);
          await bulkUpsertLink(r?.links || [], true);
          await bulkUpsertDisplayArtifact(r?.displayArtifacts || [], true);
          await bulkUpsertCallRecord(r?.callRecords || [], true);
        }),
      );
    }

    await Promise.all(contentPromises);
    await markItemsContentLoaded(feedItemIds, true);

    log.info("[DOWNLOAD CONTENT] Content loaded, emitting updates", {
      itemCount: feedItemIds.length,
    });

    emitTableUpdates(["item", "transcription", "audio_encoding", "file", "link", "display_artifact", "call_record"]);
  });

  void Promise.all(eventPromises)
    .then(() => {
      log.info("[DOWNLOAD CONTENT] Events download complete", { itemCount: feedItemIds.length });
      emitTableUpdates(["account_event"]);
    })
    .catch((error) => {
      log.error("[DOWNLOAD CONTENT] Events download failed", { error });
    });
}

export async function initializeFromAlias(tx?: Tx): Promise<void> {
  const rawSQL = sql`
  with items_from_alias as (
    select ${item.id} as id
    from ${item} 
    inner join ${workspaceMembership} 
      on ${item.accountId} = ${workspaceMembership.accountId}
    inner join ${workspaceCommandAlias} 
      on ${item.feedId} = ${workspaceCommandAlias.feedId} 
      and ${workspaceMembership.id} = ${workspaceCommandAlias.workspaceMembershipId}
  )
  update ${item}
  set "isFromAliasMember" = true
  from items_from_alias
  where ${item.id} = items_from_alias.id 
  
  `;

  await executeQuery(rawSQL, "initializeFromAlias", tx);
}

export async function initializeFromBot(tx?: Tx): Promise<void> {
  const rawSQL = sql`
  with items_from_bot as (
    select ${item.id} as id
    from ${item} 
    inner join ${account} 
      on ${item.accountId} = ${account.id} 
      and ${account.bot} = true
  )
  update ${item}
  set "isFromBot" = true
  from items_from_bot
  where ${item.id} = items_from_bot.id 
  
  `;

  await executeQuery(rawSQL, "initializeFromBot", tx);
}

export async function initializeFeedLatestActivity(tx?: Tx): Promise<void> {
  const rawSQL = sql`
  with latest_feed_items as (
    select 
    MAX(${item.createdAt}) as "latestActivity", 
    MAX(${item.createdAt}) FILTER(where ${item.isFromAliasMember} = true) as "latestAliasActivity",
    ${feed.id} as id
    from ${feed}
    inner join ${item} on ${feed.id} = ${item.feedId}
    group by ${feed.id}
  )
  update ${feed}
  set 
  "latestActivity" = GREATEST(
    NULLIF(${feed}."latestActivity", '-infinity'::timestamptz),
    latest_feed_items."latestActivity"
  ), 
  "latestAliasActivity" = GREATEST(
    NULLIF(${feed}."latestAliasActivity", '-infinity'::timestamptz),
    latest_feed_items."latestAliasActivity"
  )

  from latest_feed_items
  where ${feed.id} = latest_feed_items.id 
  
  `;

  await executeQuery(rawSQL, "initializeFeedLatestActivity", tx);
}

export async function initializeIsAliasFeed(tx?: Tx): Promise<void> {
  const rawSQL = sql`
  with alias_feed as (
    select ${feed.id} as id, ${workspaceCommandAlias.workspaceMembershipId} as workspaceMembershipId
    from ${feed}
    inner join ${workspaceCommandAlias} on ${feed.id} = ${workspaceCommandAlias.feedId}
  )
  update ${feed}
  set "isAliasFeed" = true, "aliasMembershipId" = alias_feed.workspaceMembershipId
  from alias_feed
  where ${feed.id} = alias_feed.id 
  
  `;

  await executeQuery(rawSQL, "initializeIsAliasFeed", tx);
}

export async function initializeIsAliasFeedFromIds(feedIds: string[], tx?: Tx): Promise<void> {
  const deduped = deduplicateByKeyValue(feedIds, (id) => id);
  const whereIn = sql`where ${feed.id} in (${deduped
    .map((id) => `"${id}"`)
    .join(",")
    .toString()})`;
  const rawSQL = sql`
  with alias_feed as (
    select ${feed.id} as id, ${workspaceCommandAlias.workspaceMembershipId} as workspaceMembershipId
    from ${feed}
    inner join ${workspaceCommandAlias} on ${feed.id} = ${workspaceCommandAlias.feedId}
    ${whereIn}
  )
  update ${feed}
  set "isAliasFeed" = true, "aliasMembershipId" = alias_feed.workspaceMembershipId
  from alias_feed
  where ${feed.id} = alias_feed.id 
  `;

  await executeQuery(rawSQL, "initializeIsAliasFeed", tx);
}
